<template>
  <div class="flex flex-col gap-12">
    <FormStepInputs
      :step="{
        stepName: name,
        formInputs: filteredContent,
      }"
    >
      <template #requestRsaPromoCards>
        <PromoCardMiniList v-bind="requestRsaPromoCards" :promoCards="filteredPromoCards" />
      </template>
      <template #statusMessages>
        <div class="max-w-176">
          <InformationBox v-if="rsaActiveStatusInformationBox" v-bind="rsaActiveStatusInformationBox">
            <template v-if="rsaActiveStatusInformationBox.firstLinkContent" #buttons>
              <div class="w-full pt-3">
                <span v-html="rsaActiveStatusInformationBox.firstLinkContent" />
              </div>
            </template>
          </InformationBox>
        </div>
      </template>
      <template #coverSummary>
        <ReviewSummary
          :reviewSummaries="summaryItems"
          :isConfirmationStep="false"
          :disableExpandToggle="false"
          :showDiscounts="true"
          :title="policySummary.title"
          :isSelfService="true"
          :heading="policySummary.heading"
          :belowHeading="policySummary.belowHeading"
          :subheading="policySummary.subheading"
        />
      </template>
      <template #coverFeatureSummary>
        <ReviewSummary
          v-if="
            currentPolicy?.roadsidePolicyType != RoadsideProductType.Cmo &&
            currentPolicy?.productCode !== RSA_ROADSIDE_EXCESS_USE
          "
          :reviewSummaries="coverSummaries"
          :isConfirmationStep="false"
          :disableExpandToggle="false"
          :showDiscounts="true"
          :title="coverFeatureSummary.title"
          :isSelfService="true"
          :subheading="coverFeatureSummary.subheading"
        >
          <template v-if="rsaProductContent?.infoText" #policyDiscounts>
            <div v-if="!noCoverFeatures" class="my-2 border-b"></div>
            <div class="mx-5 md:mx-8 mb-9" :class="noCoverFeatures ? '-mt-8' : 'mt-5'">
              <InformationBox
                :description="rsaProductContent?.infoText"
                :type="InformationBoxTypes.INFO"
                :hideIcon="true"
              />
            </div>
          </template>
        </ReviewSummary>
      </template>
      <template
        v-if="
          showPaymentDetails &&
          (currentPolicy?.roadsidePolicyType === RoadsideProductType.Standard ||
            currentPolicy?.productCode?.startsWith('ARN')) &&
          currentPolicy?.productCode !== RSA_ROADSIDE_EXCESS_USE
        "
        #paymentInfo
      >
        <div id="paymentDetails">
          <ReviewSummary
            :reviewSummaries="mapPaymentItems(paymentInfoSummary.fields)"
            :isConfirmationStep="false"
            :disableExpandToggle="false"
            :showDiscounts="true"
            :title="paymentInfoSummary.title"
            :isSelfService="true"
            :heading="paymentInfoSummary.heading"
            :belowHeading="paymentInfoSummary.belowHeading"
            :subheading="paymentInfoSummary.subheading"
          />
        </div>
      </template>
    </FormStepInputs>
  </div>
</template>
<script setup lang="ts">
import { computed, watch, ref, ComputedRef, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { IReviewSummary, ISitecoreFormGenericInput, OwnedAssistanceProduct } from '@/interfaces';
import { useAccountStore, useManageDirectDebitStore, useRoadsideAssistance } from '@/store';
import { getFormInput, getFormInputByName } from '@/utils/form-input-helper';
import { replaceBookmark, replaceBookmarks } from '@/utils/bookmark.replace';
import FormStepInputs from '@/components/FormStepInputs/FormStepInputs.vue';
import ReviewSummary from '@/components/ReviewSummary/ReviewSummary.vue';
import { executeEditRules } from '@/utils/edit-rules/execute-rules';
import { RSAEditRules } from '@/utils/self-service-rules/RSAEditRules';
import { RSAFirstLinkRules } from '@/utils/self-service-rules/RSAFirstLinkRules';
import { RSASubcontentRules } from '@/utils/self-service-rules/RSASubcontentRules';
import { executeDisplayRulesNoPolicyWithParams } from '@/utils/display-rules/execute-rules';
import { RSADisplayRules } from '@/utils/self-service-rules/RSADisplayRules';
import { RSAPromoCardRules } from '@/utils/self-service-rules/RSAPromoCardRules';
import { RoadsideProductType, RSA_ROADSIDE_EXCESS_USE } from '@/utils/roadside-helpers';
import { IPromoCardMini } from '@/interfaces/entities/promo-card-mini.interface';
import PromoCardMiniList from '@/components/PromoCardMini/PromoCardMiniList.vue';
import { asyncComputed } from '@vueuse/core';
import InformationBox from '@/components/InformationBox/InformationBox.vue';
import { IRoadsideContentInterface } from '@/interfaces/self-service/roadside.interface';
import { InformationBoxTypes, IInformationBox } from '@/interfaces/forms/information-box.interface';
import { PageAnalyticsBuilder } from '@/utils/page-analytics-builder';
import {
  RsaStatusInfoBox,
  RSAStatusInformationBoxRules,
} from '@/utils/self-service-rules/RSAStatusInformationBoxRules';
import { format } from 'date-fns';
import { SHORT_MONTH_DATE, LONG_MONTH_DATE } from '@/constants/formats';
import { ApttusDirectDebitStatus } from '@/interfaces/entities/apttus-direct-debit-status-response.interface';

enum Bookmarks {
  coverExpiryDate = 'coverExpiryDate',
  coverLevel = 'coverLevel',
  vehicleDetails = 'vehicleDetails',
  registrationNumber = 'registrationNumber',
  policyId = 'policyId',
  makePaymentUrl = 'makePaymentUrl',
}

const accountStore = useAccountStore();
const rsaStore = useRoadsideAssistance();
const manageDirectDebitStore = useManageDirectDebitStore();
const route = useRoute();

const props = defineProps<{
  content: ISitecoreFormGenericInput[];
  id: string;
  name: string;
}>();

const filteredContent = computed(() => {
  return props.content.filter(
    (item) =>
      item.name != 'statusMessages' ||
      (rsaStatusInfoBoxForCurrentPolicy.value !== RsaStatusInfoBox.None && rsaActiveStatusInformationBox.value)
  );
});

const policySummary = getFormInput(props.content, 'coverSummary');
const coverFeatureSummary = getFormInput(props.content, 'coverFeatureSummary');
const paymentInfoSummary = getFormInput(props.content, 'paymentInfo');
const requestRsaPromoCards = getFormInput(props.content, 'requestRsaPromoCards');

const coverSummaries = ref<IReviewSummary[]>([]);
const rsaProductContent = ref<IRoadsideContentInterface>();
const noCoverFeatures = ref<boolean>(false);
const currentPolicyTitle = ref<string>('');
const makePaymentUrl = ref<string>();
const directDebitStatus = ref<ApttusDirectDebitStatus>();
const showPaymentDetails = ref<boolean>(false);

type IExtendedInformationBox = IInformationBox & { firstLinkContent?: string };

onMounted(async () => {
  await manageDirectDebitStore.setDirectDebitDetailsFromIdentity();
  directDebitStatus.value = manageDirectDebitStore.directDebitStatus;
  const eligibleProducts = await manageDirectDebitStore.getPortfolioAssitanceProductsForDirectDebit();
  showPaymentDetails.value =
    !!currentPolicy.value && eligibleProducts.some((x) => x.assetLineItemId === currentPolicy.value?.assetLineItemId);
  makePaymentUrl.value = await getMakePaymentUrl(currentPolicy.value?.prn ?? '');
});

const currentPolicy = computed(() => {
  if (!accountStore.identityDetails?.portfolio?.assistanceProducts) return null;
  const policyId = route.query.policyId;
  const status = route.query.status;

  const usePolicyId = (policyId?.length ?? 0) > 0 ? policyId : null;
  if (!usePolicyId) return null;

  const tempPolicy = accountStore?.identityDetails?.portfolio?.assistanceProducts.find(
    (x) =>
      x.policyId === usePolicyId && (!status ? true : x.status?.toLowerCase() === status?.toString()?.toLowerCase())
  );

  return tempPolicy;
});

watch(
  () => currentPolicy.value?.prn,
  async (newVal) => {
    if (newVal) {
      makePaymentUrl.value = await getMakePaymentUrl(newVal);
    }
  }
);

const formatItem = (item: any, currentPolicy: OwnedAssistanceProduct) => {
  item.fields.content.value = replaceBookmark(
    Bookmarks.coverExpiryDate,
    item.fields.content.value,
    format(currentPolicy.endDate, SHORT_MONTH_DATE)
  );
  item.fields.content.value = replaceBookmark(
    Bookmarks.coverLevel,
    item.fields.content.value,
    currentPolicyTitle.value
  );
  if (currentPolicy.vehicleDetail) {
    item.fields.content.value = replaceBookmark(
      Bookmarks.vehicleDetails,
      item.fields.content.value,
      `${currentPolicy.vehicleDetail.manufacturingYear} ${currentPolicy.vehicleDetail.make.description} ${currentPolicy.vehicleDetail.model?.description ?? ''} ${currentPolicy.vehicleDetail.body?.colour ?? ''}`
    );
    item.fields.content.value = replaceBookmark(
      Bookmarks.registrationNumber,
      item.fields.content.value,
      currentPolicy.vehicleDetail.registrationNumber
    );
  }
  // Replace policy id in url
  if (item.fields.primaryLink.value?.querystring) {
    item.fields.primaryLink.value.querystring = replaceBookmark(
      Bookmarks.policyId,
      item.fields.primaryLink.value.querystring,
      currentPolicy.policyId!
    );
  }
  if (item.fields.subcontent?.value) {
    item.fields.subcontent.value = replaceBookmark(
      Bookmarks.policyId,
      item.fields.subcontent.value,
      currentPolicy.policyId!
    );
  }
  item.showPrimaryLink = executeEditRules(RSAEditRules, item.fields.editRules, currentPolicy);
  item.showFirstLink = executeEditRules(RSAFirstLinkRules, item.fields.firstLinkRules, currentPolicy);
  item.showSubcontent = executeEditRules(RSASubcontentRules, item.fields.subcontentRules, currentPolicy);

  return item;
};

const formatCoverItem = async (item: any, currentPolicy: OwnedAssistanceProduct) => {
  rsaProductContent.value = await rsaStore.getRsaProductContent(currentPolicy.productCode);
  const content = replaceBookmarks(item.fields.content.value, rsaProductContent.value);
  if (!content || content === '-') {
    return null; // clearly signal to drop this item
  }

  item.fields.content.value = content;
  return item;
};

const showOnlinePromoCard = computed(
  () => currentPolicy.value && RSAPromoCardRules.showOnlinePromoCard(currentPolicy.value as OwnedAssistanceProduct)
);
const showPhonePromoCard = computed(
  () => currentPolicy.value && RSAPromoCardRules.showPhonePromoCard(currentPolicy.value as OwnedAssistanceProduct)
);

const filteredPromoCards = computed(() => {
  let promoCards: Partial<IPromoCardMini & { name: string }>[] = requestRsaPromoCards.fields;
  if (promoCards.length > 0) {
    if (!showOnlinePromoCard.value) {
      promoCards = promoCards.filter((p) => p.name?.toLowerCase() !== 'onlineform');
    }

    if (!showPhonePromoCard.value) {
      promoCards = promoCards.filter((p) => p.name?.toLowerCase() !== 'phone');
    }
  }

  return promoCards;
});

const summaryItems = asyncComputed(async () => {
  if (!currentPolicy.value) return [];

  currentPolicyTitle.value =
    currentPolicy.value?.roadsidePolicyType !== RoadsideProductType.Cmo
      ? (currentPolicy.value?.tierDescription ?? '')
      : ((await rsaStore.getRsaProductContent(currentPolicy.value?.productCode))?.tierDescription ?? '');

  return policySummary.fields.map((summary: any) => {
    return {
      ...summary,
      items: summary.items
        .filter((item: any) =>
          executeDisplayRulesNoPolicyWithParams(
            RSADisplayRules,
            item.fields.displayRules,
            currentPolicy.value!,
            directDebitStatus.value!
          )
        )
        .map((item: any) => formatItem(item, currentPolicy.value!)),
    };
  });
});

const getMakePaymentUrl = async (prn: string) => {
  const selfServiceMakePaymentUrl = await accountStore.getSelfServiceMakePaymentUrl();
  return `${selfServiceMakePaymentUrl?.replace('{0}', prn)}`;
};

const rsaStatusInfoBoxForCurrentPolicy = computed(() => {
  return RSAStatusInformationBoxRules.getRsaStatusInfoBox(currentPolicy.value as OwnedAssistanceProduct);
});

const rsaActiveStatusInformationBox: ComputedRef<IExtendedInformationBox | undefined> = computed(() => {
  const statusMessages = getFormInputByName(props.content, 'statusMessages');
  const statusInfoBoxInput = getFormInput(statusMessages.fields, rsaStatusInfoBoxForCurrentPolicy.value);
  if (statusInfoBoxInput) {
    return {
      title: statusInfoBoxInput?.title?.value,
      description: replaceBookmark(
        Bookmarks.coverExpiryDate,
        statusInfoBoxInput?.content?.value,
        format(currentPolicy.value?.endDate ?? '', LONG_MONTH_DATE)
      ),
      type: statusInfoBoxInput?.messageType?.value,
      isCircle: true,
      useButtonSlot: [RsaStatusInfoBox.RenewalPaymentDue, RsaStatusInfoBox.PaymentOverdue].includes(
        rsaStatusInfoBoxForCurrentPolicy.value
      ),
      firstLinkContent: replaceBookmark(
        Bookmarks.makePaymentUrl,
        statusInfoBoxInput?.firstLinkContent?.value,
        makePaymentUrl.value ?? ''
      ),
    };
  }

  return undefined;
});

const mapCoverSummaryItems = async (summaries: any) => {
  if (!currentPolicy.value) return [];

  const resolved = await Promise.all(
    summaries.map(async (summary: any) => {
      const items = (
        await Promise.all(summary.items.map((item: any) => formatCoverItem(item, currentPolicy.value!)))
      ).filter(Boolean); // remove null/undefined items

      return { ...summary, items };
    })
  );
  if (rsaProductContent.value?.infoText && resolved[0].items?.length <= 0) {
    noCoverFeatures.value = true;
    resolved[0].items.push({}); //to show icon on the review summary component
  } else {
    noCoverFeatures.value = false;
  }
  return resolved;
};

const mapPaymentItems = (summaries: any) => {
  if (!currentPolicy.value) return [];

  return summaries.map((summary: any) => {
    return {
      ...summary,
      items: summary.items
        .map((item: any) => {
          return {
            ...item,
            showPrimaryLink: executeEditRules(RSAEditRules, item.fields.editRules, currentPolicy.value!),
          };
        })
        .filter((item: any) =>
          executeDisplayRulesNoPolicyWithParams(
            RSADisplayRules,
            item.fields.displayRules,
            currentPolicy.value!,
            directDebitStatus.value!
          )
        ),
    };
  });
};

const pushAnalytics = () => {
  const pageAnalytics = new PageAnalyticsBuilder('Roadside product details');
  pageAnalytics.populateRsaProductData(currentPolicy.value!);
  pageAnalytics.pushAnalytics();
};

watch(
  currentPolicy,
  async (policy: OwnedAssistanceProduct | null | undefined, oldValue) => {
    if (!policy) {
      coverSummaries.value = [];
      return;
    }
    !oldValue && pushAnalytics();
    coverSummaries.value = await mapCoverSummaryItems(coverFeatureSummary.fields);
  },
  { immediate: true } // if policy is already set when component mounts, run immediately
);
</script>
